import { isNumber } from 'lodash';

import { AlertText, ATTENTION_TODAY, IMMEDIATE_ATTENTION } from 'constants/alertBadge.const';

import { AlertUrgency } from 'constants/urgency.consts';

import { ConditionType, PopulationType } from 'models/ClinicianAlert';
import ClinicianAlert from 'models/ClinicianAlert';
import ClinicianAlertCondition from 'models/Conditions/ClinicianAlertCondition';
import { NumericReportedCause, ReportedCause } from 'models/ReportedCause';
import SeverityReportedCause from 'models/ReportedCause';
import { TicketTypeKind } from 'models/TicketTypes';

import { TicketFormField } from 'components/Ticket/TicketForms/ticket.shared';

import {
  alertUrgencyToTextAndValueMap,
  shouldUpdateUrgency,
  SymptomTicketUrgencyValueAndText
} from './urgencyMappings';

// Get badge text for clinician alert urgency
export const getClinicianAlertBadgeText = (urgency: AlertUrgency): AlertText | null => {
  switch (urgency) {
    case AlertUrgency.CriticalAlert:
      return IMMEDIATE_ATTENTION;
    case AlertUrgency.UrgentAlert:
      return ATTENTION_TODAY;
    default:
      return null;
  }
};

// Check if a condition matches a symptom
export const isConditionMatchForSymptom = (
  condition: ClinicianAlertCondition,
  symptom: ReportedCause | number
): boolean => {
  if (symptom === undefined) {
    return false;
  }

  // Used by symptom operator ticket which doesn't have severity, so we send just the symptom id
  if (isNumber(symptom)) {
    return condition.getId() === symptom;
  }

  const conditionId = condition.getId();
  const threshold = condition.getBottomThreshold();
  const hasSameId = conditionId === symptom.causeId;

  // Check if IDs match and either severity or value meets the threshold
  return (
    hasSameId &&
    ((symptom as SeverityReportedCause).severity >= threshold ||
      ((symptom as NumericReportedCause).value as number) >= threshold)
  );
};

export const isAlertApplicableToPatient = (alert: ClinicianAlert, patientId: number): boolean => {
  const { type: populationType } = alert.population;

  // If it's for specific patients, check if this patient is in the list
  if (populationType === PopulationType.SpecificPatients) {
    return alert.population.patientIds.includes(patientId);
  }

  return populationType === PopulationType.All;
};

export const hasMatchingSymptomForAlert = (
  alert: ClinicianAlert,
  symptomIds: number[]
): boolean => {
  const hasAnyCauseCondition = alert.conditions.some(
    (condition) => condition.type === ConditionType.AnyCause
  );

  if (hasAnyCauseCondition) {
    return true;
  }

  return symptomIds.some((symptomId) =>
    alert.conditions.some((condition) => isConditionMatchForSymptom(condition, symptomId))
  );
};

export const isSymptomTicket = (ticket: TicketFormField): boolean => {
  return (
    Array.isArray(ticket.ticketTypeSelectorValue) ||
    ticket.ticketTypeSelectorValue?.parentKind === TicketTypeKind.symptom
  );
};

export const getSelectedSymptomIds = (tickets: TicketFormField[] | undefined): number[] => {
  if (!tickets) return [];

  const symptomTicket = tickets.find((ticket) => isSymptomTicket(ticket));

  if (!symptomTicket) return [];

  return symptomTicket.ticketSubTypeIds.map((id) =>
    typeof id === 'string' ? parseInt(id, 10) : id
  );
};

export const hasSymptomTicket = (tickets: TicketFormField[] | undefined): boolean => {
  if (!tickets) return false;
  return tickets.some((ticket) => isSymptomTicket(ticket));
};

export type ClinicianAlertsResult = {
  highestUrgency: SymptomTicketUrgencyValueAndText | null;
  alertTriggeredBySymptomId: Map<number, AlertUrgency>;
};

export const checkForMatchingClinicianAlerts = (
  tickets: TicketFormField[] | undefined,
  patientId: number,
  globalAlerts: ClinicianAlert[]
): ClinicianAlertsResult => {
  const symptomIds = getSelectedSymptomIds(tickets);
  if (!symptomIds.length) {
    return {
      highestUrgency: null,
      alertTriggeredBySymptomId: new Map()
    };
  }

  const matchingAlerts = getMatchingAlertsForSymptoms(symptomIds, patientId, globalAlerts);

  if (!matchingAlerts.length) {
    return {
      highestUrgency: null,
      alertTriggeredBySymptomId: new Map()
    };
  }

  let highestUrgency: AlertUrgency | null = null;
  const alertTriggeredBySymptomId = new Map<number, AlertUrgency>();

  matchingAlerts.forEach((alert: ClinicianAlert) => {
    if (shouldUpdateUrgency(alert.urgency, highestUrgency)) {
      highestUrgency = alert.urgency;
    }

    const hasAnyCauseCondition = alert.conditions.some(
      (condition) => condition.type === ConditionType.AnyCause
    );

    symptomIds.forEach((symptomId) => {
      const symptomTriggeredAlert =
        hasAnyCauseCondition ||
        alert.conditions.some((condition) => isConditionMatchForSymptom(condition, symptomId));

      if (symptomTriggeredAlert) {
        // Only update the map if there's no existing urgency or the new urgency is higher
        const existingUrgency = alertTriggeredBySymptomId.get(symptomId);
        if (!existingUrgency || shouldUpdateUrgency(alert.urgency, existingUrgency)) {
          alertTriggeredBySymptomId.set(symptomId, alert.urgency);
        }
      }
    });
  });

  // Return the highest urgency value and text, along with the symptom-to-alert mapping
  return {
    highestUrgency:
      highestUrgency && highestUrgency in alertUrgencyToTextAndValueMap
        ? alertUrgencyToTextAndValueMap[highestUrgency]
        : null,
    alertTriggeredBySymptomId
  };
};

export const getMatchingAlertsForSymptoms = (
  symptomIds: number[],
  patientId: number,
  globalAlerts: ClinicianAlert[]
): ClinicianAlert[] => {
  if (!symptomIds.length) {
    return [];
  }

  const operatorIncludeRules = globalAlerts.filter(
    (alert: ClinicianAlert) => alert.isIncludeOperator
  );

  if (!operatorIncludeRules.length) {
    return [];
  }

  return operatorIncludeRules.filter(
    (alert: ClinicianAlert) =>
      isAlertApplicableToPatient(alert, patientId) && hasMatchingSymptomForAlert(alert, symptomIds)
  );
};

export interface TriggeredSymptomInfo {
  id: number;
  name: string;
  alertText: AlertText;
}

export const getTriggeredSymptomsInfo = (
  ticketSubTypeIds: (string | number)[],
  alertTriggeredBySymptomId: Map<number, AlertUrgency>,
  getCauseNameById: (id: number) => string
): TriggeredSymptomInfo[] => {
  if (!ticketSubTypeIds || !ticketSubTypeIds.length) {
    return [];
  }

  const triggeredSymptomsIds = ticketSubTypeIds
    .map(Number)
    .filter((ticketSubTypeId: number) => alertTriggeredBySymptomId.has(ticketSubTypeId));

  return triggeredSymptomsIds
    .map((symptomId: number) => {
      const urgency = alertTriggeredBySymptomId.get(symptomId);
      const alertText = urgency ? getClinicianAlertBadgeText(urgency) : null;

      if (!alertText) {
        return null;
      }

      return {
        id: symptomId,
        name: getCauseNameById(symptomId),
        alertText: alertText
      };
    })
    .filter(Boolean) as TriggeredSymptomInfo[];
};
